/**
 * SenseLifting Sound Engine v2
 * Full themed audio experience — paid users only.
 * Freemium accounts receive no sound output.
 *
 * Sound events:
 *   'nav'            — main screen / tab switch (impressive transition)
 *   'tab'            — sub-tab switch within a screen (lighter)
 *   'filter'         — pill/filter/category selection (shorter, higher than tab)
 *   'click'          — generic button interaction (subtle)
 *   'message'        — incoming chat message
 *   'message_sent'   — outgoing chat message
 *   'game_start'     — game or session launch
 *   'game_win'       — game completed / result shown
 *   'exercise_start' — exercise session begins
 *   'exercise_done'  — exercise session completed
 *   'gift'           — credits / reward received
 *   'notif'          — new notification
 *
 * Themes: tech · soft · nature · arcade
 * Ambient: startAmbient(themeId?) / stopAmbient()
 * TTS:     speakInstruction(text, lang) / stopSpeech()
 */

// ── License gate ─────────────────────────────────────────────────────────────

function isPaidUser() {
  try {
    const raw = localStorage.getItem('sl_license');
    const lic = raw ? JSON.parse(raw) : null;
    return lic && lic !== 'freemium';
  } catch { return false; }
}

// ── Audio helpers ─────────────────────────────────────────────────────────────

function getCtx() {
  try { return new (window.AudioContext || window.webkitAudioContext)(); }
  catch { return null; }
}

function closeCtx(ctx, ms = 1400) {
  if (!ctx) return;
  setTimeout(() => { try { ctx.close(); } catch {} }, ms);
}

/** Single oscillator with optional frequency sweep and envelope. */
function osc(ctx, freq, t, dur, vol = 0.28, type = 'sine', freqEnd = null) {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.connect(g); g.connect(ctx.destination);
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (freqEnd != null) o.frequency.linearRampToValueAtTime(freqEnd, t + dur);
  g.gain.setValueAtTime(vol, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.start(t); o.stop(t + dur + 0.02);
}

/** Multiple oscillators at once (chord). */
function chord(ctx, freqs, t, dur, vol = 0.12, type = 'sine') {
  freqs.forEach(f => osc(ctx, f, t, dur, vol, type));
}

/** Sequenced oscillators (arpeggio). */
function seq(ctx, notes, t, gap, dur, vol = 0.22, type = 'sine') {
  notes.forEach((f, i) => osc(ctx, f, t + i * gap, dur, vol, type));
}

/** Short noise burst (percussion, rustles, water). */
function noise(ctx, t, dur, vol = 0.18) {
  try {
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d   = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(g); g.connect(ctx.destination);
    src.start(t);
  } catch {}
}

/** Long sustained noise (wind / ambient background). */
function windNoise(ctx, t, dur, vol = 0.04) {
  try {
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d   = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    // Low-pass filter for wind feel
    const lpf = ctx.createBiquadFilter();
    lpf.type = 'lowpass'; lpf.frequency.setValueAtTime(400, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + dur * 0.15);
    g.gain.setValueAtTime(vol, t + dur * 0.8);
    g.gain.linearRampToValueAtTime(0, t + dur);
    src.connect(lpf); lpf.connect(g); g.connect(ctx.destination);
    src.start(t);
  } catch {}
}

// ── Theme sound definitions ───────────────────────────────────────────────────

export const SOUND_THEME_DEFS = {
  tech:      { name: 'Tech',       nameNL: 'Tech',       icon: '🤖', desc: 'Futuristic electronic',   descNL: 'Futuristisch elektronisch', color: '#5B9ED0' },
  soft:      { name: 'Soft',       nameNL: 'Zacht',      icon: '🌙', desc: 'Minimal & gentle',        descNL: 'Minimaal & zacht',          color: '#7B6FD0' },
  nature:    { name: 'Nature',     nameNL: 'Natuur',     icon: '🌿', desc: 'Organic & natural',       descNL: 'Organisch & natuurlijk',    color: '#2A9E82' },
  arcade:    { name: 'Arcade',     nameNL: 'Arcade',     icon: '🕹️', desc: 'Retro game-style',        descNL: 'Retro game-stijl',          color: '#C8A84B' },
  jazz:      { name: 'Jazz',       nameNL: 'Jazz',       icon: '🎷', desc: 'Warm jazz tones',         descNL: 'Warme jazzklanken',         color: '#D4873A' },
  epic:      { name: 'Epic',       nameNL: 'Episch',     icon: '⚔️', desc: 'Cinematic orchestral',    descNL: 'Cinematisch orkest',        color: '#C0392B',  gameLvl: 8  },
  rain:      { name: 'Rain',       nameNL: 'Regen',      icon: '🌧️', desc: 'Calming rain & drops',    descNL: 'Rustgevende regen',         color: '#5B9ED0' },
  meditation:{ name: 'Meditation', nameNL: 'Meditatie',  icon: '🧘', desc: 'Tibetan bowls & silence', descNL: 'Tibetaanse klankschalen',   color: '#C8A84B',  gameLvl: 12 },
  cosmos:    { name: 'Cosmos',     nameNL: 'Kosmos',     icon: '🌌', desc: 'Deep space ambient',      descNL: 'Diepe ruimte ambient',      color: '#7B6FD0',  gameLvl: 6  },
  dojo:      { name: 'Dojo',       nameNL: 'Dojo',       icon: '🥋', desc: 'Focus & discipline',      descNL: 'Focus & discipline',        color: '#C8A84B',  gameLvl: 10 },
};
export const SOUND_THEME_IDS = Object.keys(SOUND_THEME_DEFS);

// ─────────────────────────────────────────────────────────────────────────────

const themes = {

  // ══ TECH ══════════════════════════════════════════════════════════════════
  tech: {
    // Long electronic sweep + resolution chord  ─ very impressive transition
    nav: (ctx, t) => {
      osc(ctx, 180, t, 0.22, 0.22, 'sine', 900);      // low→high sweep
      osc(ctx, 900, t + 0.22, 0.35, 0.18, 'sine');     // sustain note
      chord(ctx, [440, 660, 880], t + 0.25, 0.45, 0.08); // resolution chord
      osc(ctx, 1320, t + 0.30, 0.12, 0.06, 'sine');    // shimmer
    },
    // Quick 2-tone electronic confirm  ─ shorter than nav
    tab: (ctx, t) => {
      osc(ctx, 600, t,        0.07, 0.18, 'sine', 900);
      osc(ctx, 900, t + 0.08, 0.09, 0.14, 'sine');
    },
    // Micro upward blip — instant selection confirm
    filter: (ctx, t) => osc(ctx, 1100, t, 0.04, 0.13, 'sine', 1700),
    // Tiny downward sweep  ─ barely noticeable
    click: (ctx, t) => osc(ctx, 700, t, 0.055, 0.18, 'sine', 350),
    // 3 ascending electronic pings
    message: (ctx, t) => {
      osc(ctx, 440, t,        0.08, 0.18, 'sine');
      osc(ctx, 660, t + 0.09, 0.08, 0.16, 'sine');
      osc(ctx, 880, t + 0.18, 0.10, 0.14, 'sine');
    },
    // Quick 2-step outgoing data burst
    message_sent: (ctx, t) => {
      osc(ctx, 800, t,        0.06, 0.14, 'sine', 1200);
      osc(ctx, 1200, t + 0.07, 0.05, 0.08, 'sine');
    },
    // 4-step power-up + resolving chord
    game_start: (ctx, t) => {
      seq(ctx, [220, 330, 440, 660], t, 0.09, 0.12, 0.22, 'sine');
      chord(ctx, [440, 660, 880], t + 0.38, 0.5, 0.10);
    },
    // Victory arpeggio 5 notes + top note hold
    game_win: (ctx, t) => {
      seq(ctx, [440, 523, 659, 784, 1047], t, 0.09, 0.14, 0.22, 'sine');
      osc(ctx, 1047, t + 0.46, 0.55, 0.15, 'sine');
      chord(ctx, [523, 784, 1047], t + 0.50, 0.50, 0.07);
    },
    // Boot sequence: 3 ascending + sustained resonance + shimmer
    exercise_start: (ctx, t) => {
      seq(ctx, [330, 440, 523], t, 0.12, 0.18, 0.18, 'sine');
      chord(ctx, [440, 660, 880], t + 0.40, 0.70, 0.08);
      osc(ctx, 1320, t + 0.50, 0.35, 0.04, 'sine');
    },
    // Descending resolution + final chord
    exercise_done: (ctx, t) => {
      seq(ctx, [880, 659, 523, 440], t, 0.12, 0.14, 0.18, 'sine');
      chord(ctx, [440, 523, 659], t + 0.52, 0.60, 0.10);
    },
    // 4 ascending chimes
    gift: (ctx, t) => seq(ctx, [440, 554, 659, 880], t, 0.10, 0.18, 0.20, 'sine'),
    // Two ascending blips — "correct!" feedback
    game_correct: (ctx, t) => { osc(ctx, 784, t, 0.07, 0.16, 'sine', 1047); osc(ctx, 1047, t + 0.08, 0.09, 0.14, 'sine'); },
    // Low descending buzz — "wrong" feedback
    game_wrong: (ctx, t) => { osc(ctx, 400, t, 0.05, 0.15, 'sine', 160); osc(ctx, 180, t + 0.06, 0.11, 0.12, 'sine'); },
    // Single precise electronic ping
    notif: (ctx, t) => osc(ctx, 880, t, 0.08, 0.16, 'sine'),
  },

  // ══ SOFT ══════════════════════════════════════════════════════════════════
  soft: {
    // Two gentle chords layering softly  ─ graceful screen change
    nav: (ctx, t) => {
      chord(ctx, [261, 392, 523], t, 0.55, 0.07, 'sine');      // C4+G4+C5
      chord(ctx, [392, 523, 784], t + 0.18, 0.60, 0.07, 'sine'); // G4+C5+G5 bloom
    },
    // Single quiet bell  ─ lighter than nav
    tab: (ctx, t) => osc(ctx, 1047, t, 0.18, 0.07, 'sine'),
    // Two tiny bells — soft "tick-tick" select
    filter: (ctx, t) => { osc(ctx, 880, t, 0.04, 0.05, 'sine'); osc(ctx, 1174, t + 0.045, 0.04, 0.04, 'sine'); },
    // Barely-there sine pulse
    click: (ctx, t) => osc(ctx, 523, t, 0.035, 0.06, 'sine'),
    // Warm descending ding
    message: (ctx, t) => osc(ctx, 1174, t, 0.22, 0.10, 'sine', 784),
    // Lower warm ding (outgoing)
    message_sent: (ctx, t) => osc(ctx, 784, t, 0.18, 0.08, 'sine', 523),
    // C-E-G warm ascending arpeggio
    game_start: (ctx, t) => seq(ctx, [523, 659, 784], t, 0.14, 0.24, 0.10, 'sine'),
    // Full chord bloom + high note
    game_win: (ctx, t) => {
      chord(ctx, [523, 659, 784, 1047], t, 0.65, 0.08, 'sine');
      osc(ctx, 1568, t + 0.20, 0.45, 0.05, 'sine');
    },
    // Warm opening chord
    exercise_start: (ctx, t) => {
      chord(ctx, [261, 392, 523, 659], t, 0.80, 0.06, 'sine');
      osc(ctx, 1047, t + 0.35, 0.40, 0.04, 'sine');
    },
    // Descending resolution
    exercise_done: (ctx, t) => seq(ctx, [784, 659, 523, 392], t, 0.15, 0.22, 0.09, 'sine'),
    // Full major chord sustained
    gift: (ctx, t) => chord(ctx, [523, 659, 784, 1047], t, 0.48, 0.08, 'sine'),
    // Light bell notification
    notif: (ctx, t) => osc(ctx, 659, t, 0.20, 0.08, 'sine'),
  },

  // ══ NATURE ════════════════════════════════════════════════════════════════
  nature: {
    // Wind chime cascade 4 notes + noise breath  ─ entering a new space
    nav: (ctx, t) => {
      noise(ctx, t, 0.12, 0.08);
      seq(ctx, [1047, 1319, 1568, 2093], t + 0.06, 0.11, 0.30, 0.10, 'sine');
    },
    // Water drop (descending sweep)
    tab: (ctx, t) => osc(ctx, 1400, t, 0.13, 0.13, 'sine', 600),
    // Tiny chirp — like a small insect click or pebble tap
    filter: (ctx, t) => osc(ctx, 1800, t, 0.04, 0.07, 'sine', 2400),
    // Short noise tap
    click: (ctx, t) => noise(ctx, t, 0.04, 0.16),
    // Higher water-drop splash
    message: (ctx, t) => osc(ctx, 1600, t, 0.14, 0.13, 'sine', 700),
    // Leaf rustle (noise burst, different from click)
    message_sent: (ctx, t) => noise(ctx, t, 0.08, 0.12),
    // Three ascending bird chirps
    game_start: (ctx, t) => {
      osc(ctx, 900, t,        0.09, 0.13, 'sine', 1300);
      osc(ctx, 900, t + 0.14, 0.09, 0.13, 'sine', 1300);
      osc(ctx, 900, t + 0.28, 0.11, 0.15, 'sine', 1600);
    },
    // 6-note wind-chime celebration
    game_win: (ctx, t) => {
      seq(ctx, [1047, 1319, 1568, 1760, 2093, 2637], t, 0.10, 0.28, 0.09, 'sine');
      noise(ctx, t + 0.08, 0.20, 0.06);
    },
    // Deep bowl/gong resonance
    exercise_start: (ctx, t) => {
      osc(ctx, 130, t, 1.2, 0.18, 'sine');
      osc(ctx, 260, t, 0.9, 0.06, 'sine');
      osc(ctx, 390, t + 0.15, 0.5, 0.04, 'sine');
    },
    // Chimes + bird call
    exercise_done: (ctx, t) => {
      seq(ctx, [1047, 1319, 1568], t, 0.13, 0.30, 0.09, 'sine');
      osc(ctx, 1100, t + 0.45, 0.10, 0.10, 'sine', 1600);
    },
    // Wind-chime cascade
    gift: (ctx, t) => seq(ctx, [1047, 1319, 1568, 2093], t, 0.13, 0.28, 0.10, 'sine'),
    // Soft water-tap notification
    notif: (ctx, t) => osc(ctx, 1200, t, 0.09, 0.11, 'sine', 900),
  },

  // ══ ARCADE ════════════════════════════════════════════════════════════════
  arcade: {
    // Square-wave screen slide + blip resolve  ─ classic menu transition
    nav: (ctx, t) => {
      osc(ctx, 200, t, 0.16, 0.22, 'square', 800);
      osc(ctx, 800, t + 0.17, 0.12, 0.20, 'square');
      osc(ctx, 1000, t + 0.30, 0.07, 0.16, 'square');
    },
    // Two quick square blips (menu navigate)
    tab: (ctx, t) => {
      osc(ctx, 600, t,        0.045, 0.20, 'square');
      osc(ctx, 800, t + 0.06, 0.045, 0.18, 'square');
    },
    // Single high square tone — inventory/item select
    filter: (ctx, t) => osc(ctx, 1000, t, 0.035, 0.16, 'square'),
    // Single square blip
    click: (ctx, t) => osc(ctx, 400, t, 0.04, 0.22, 'square'),
    // Incoming: two rising blips
    message: (ctx, t) => {
      osc(ctx, 600, t,        0.055, 0.22, 'square');
      osc(ctx, 800, t + 0.065, 0.055, 0.20, 'square');
    },
    // Outgoing: quick upward square sweep
    message_sent: (ctx, t) => osc(ctx, 700, t, 0.07, 0.18, 'square', 1100),
    // Level-start fanfare: 5-step ascending + chord blast
    game_start: (ctx, t) => {
      seq(ctx, [220, 330, 440, 660, 880], t, 0.07, 0.09, 0.22, 'square');
      chord(ctx, [440, 880], t + 0.38, 0.25, 0.12, 'square');
    },
    // Victory jingle: arp + chord + fanfare blip
    game_win: (ctx, t) => {
      seq(ctx, [523, 659, 784], t, 0.10, 0.09, 0.24, 'square');
      chord(ctx, [523, 784, 1047], t + 0.32, 0.30, 0.10, 'square');
      osc(ctx, 1568, t + 0.46, 0.24, 0.14, 'square');
    },
    // Select / ready sound
    exercise_start: (ctx, t) => {
      seq(ctx, [440, 660, 880], t, 0.10, 0.10, 0.22, 'square');
      osc(ctx, 1047, t + 0.32, 0.12, 0.14, 'square');
    },
    // Stage-clear jingle
    exercise_done: (ctx, t) => seq(ctx, [880, 1047, 1319, 1568], t, 0.10, 0.11, 0.22, 'square'),
    // Victory fanfare
    gift: (ctx, t) => {
      seq(ctx, [523, 659, 784], t, 0.10, 0.09, 0.26, 'square');
      osc(ctx, 1047, t + 0.30, 0.22, 0.28, 'square');
    },
    // Single blip
    notif: (ctx, t) => osc(ctx, 600, t, 0.05, 0.20, 'square'),
  },

  // ══ JAZZ ══════════════════════════════════════════════════════════════════
  jazz: {
    // Warm ii-V-I chord progression feel
    nav: (ctx, t) => {
      // Dm7 → G7 → Cmaj7
      chord(ctx, [293, 370, 440, 523], t,        0.50, 0.07, 'sine'); // Dm7
      chord(ctx, [196, 247, 370, 494], t + 0.28, 0.50, 0.06, 'sine'); // G7
      chord(ctx, [261, 329, 392, 523], t + 0.56, 0.65, 0.08, 'sine'); // Cmaj7
    },
    tab: (ctx, t) => {
      osc(ctx, 370, t,        0.12, 0.10, 'sine', 440); // F→A glide
      osc(ctx, 523, t + 0.14, 0.16, 0.07, 'sine');
    },
    filter: (ctx, t) => osc(ctx, 554, t, 0.07, 0.09, 'sine', 659), // E→F# blip
    click:  (ctx, t) => osc(ctx, 247, t, 0.06, 0.10, 'sine'),      // B low pluck
    message: (ctx, t) => {
      osc(ctx, 440, t,        0.14, 0.10, 'sine');
      osc(ctx, 554, t + 0.12, 0.14, 0.08, 'sine');
      osc(ctx, 659, t + 0.24, 0.18, 0.09, 'sine');
    },
    message_sent: (ctx, t) => {
      osc(ctx, 659, t,        0.10, 0.08, 'sine');
      osc(ctx, 554, t + 0.12, 0.10, 0.07, 'sine');
    },
    game_start: (ctx, t) => {
      // Swing-feel ascending line
      seq(ctx, [261, 329, 370, 440, 494, 523], t, 0.09, 0.15, 0.12, 'sine');
      chord(ctx, [261, 329, 392, 523], t + 0.60, 0.60, 0.08, 'sine');
    },
    game_win: (ctx, t) => {
      seq(ctx, [523, 659, 784, 1047], t, 0.10, 0.16, 0.12, 'sine');
      chord(ctx, [523, 659, 784, 1047], t + 0.45, 0.70, 0.08, 'sine');
      osc(ctx, 1568, t + 0.60, 0.40, 0.05, 'sine');
    },
    exercise_start: (ctx, t) => {
      chord(ctx, [196, 247, 294, 370], t,        0.70, 0.07, 'sine'); // Gm7
      chord(ctx, [261, 329, 392, 523], t + 0.45, 0.70, 0.08, 'sine'); // Cmaj7
    },
    exercise_done: (ctx, t) => {
      seq(ctx, [784, 659, 523, 440, 392], t, 0.12, 0.18, 0.10, 'sine');
      chord(ctx, [261, 329, 392, 523], t + 0.62, 0.55, 0.08, 'sine');
    },
    gift: (ctx, t) => {
      chord(ctx, [440, 523, 659, 784], t,        0.50, 0.08, 'sine');
      chord(ctx, [523, 659, 784, 1047],t + 0.30, 0.60, 0.07, 'sine');
    },
    notif: (ctx, t) => { osc(ctx, 554, t, 0.12, 0.10, 'sine'); osc(ctx, 659, t + 0.14, 0.12, 0.08, 'sine'); },
  },

  // ══ EPIC ══════════════════════════════════════════════════════════════════
  epic: {
    // Big cinematic brass + string build
    nav: (ctx, t) => {
      osc(ctx, 65,  t, 0.55, 0.22, 'sawtooth');    // deep bass
      osc(ctx, 130, t, 0.55, 0.10, 'sawtooth');    // bass octave
      chord(ctx, [220, 277, 330], t + 0.15, 0.65, 0.09, 'sine'); // 2nd chord
      chord(ctx, [330, 415, 523], t + 0.45, 0.70, 0.10, 'sine'); // 3rd chord (resolve)
      osc(ctx, 1047, t + 0.55, 0.35, 0.04, 'sine'); // shimmer
    },
    tab: (ctx, t) => {
      osc(ctx, 220, t,        0.12, 0.14, 'sawtooth');
      osc(ctx, 330, t + 0.13, 0.15, 0.10, 'sine');
    },
    filter: (ctx, t) => osc(ctx, 440, t, 0.06, 0.12, 'sawtooth', 880),
    click:  (ctx, t) => osc(ctx, 165, t, 0.07, 0.16, 'sawtooth'),
    message: (ctx, t) => {
      osc(ctx, 330, t,        0.14, 0.14, 'sine');
      osc(ctx, 415, t + 0.15, 0.16, 0.12, 'sine');
    },
    message_sent: (ctx, t) => osc(ctx, 440, t, 0.12, 0.12, 'sawtooth', 660),
    game_start: (ctx, t) => {
      osc(ctx, 65, t, 0.8, 0.20, 'sawtooth');
      seq(ctx, [165, 220, 277, 330, 440], t + 0.05, 0.10, 0.18, 0.12, 'sine');
      chord(ctx, [220, 277, 330, 440], t + 0.58, 0.70, 0.10, 'sine');
    },
    game_win: (ctx, t) => {
      osc(ctx, 65, t, 1.0, 0.18, 'sawtooth');
      seq(ctx, [330, 415, 523, 659, 784], t, 0.09, 0.18, 0.12, 'sine');
      chord(ctx, [330, 415, 523, 659, 784], t + 0.50, 0.80, 0.08, 'sine');
      osc(ctx, 1047, t + 0.70, 0.50, 0.05, 'sine');
    },
    exercise_start: (ctx, t) => {
      osc(ctx, 82, t, 0.70, 0.18, 'sawtooth');
      chord(ctx, [165, 220, 277], t + 0.20, 0.70, 0.10, 'sine');
      osc(ctx, 659, t + 0.55, 0.40, 0.05, 'sine');
    },
    exercise_done: (ctx, t) => {
      seq(ctx, [523, 659, 784, 659, 523], t, 0.13, 0.18, 0.12, 'sine');
      chord(ctx, [261, 330, 392, 523], t + 0.68, 0.70, 0.10, 'sine');
    },
    gift: (ctx, t) => {
      osc(ctx, 65, t, 0.8, 0.15, 'sawtooth');
      seq(ctx, [523, 659, 784, 1047], t + 0.10, 0.11, 0.18, 0.12, 'sine');
    },
    notif: (ctx, t) => { osc(ctx, 220, t, 0.10, 0.16, 'sawtooth'); osc(ctx, 330, t + 0.12, 0.12, 0.10, 'sine'); },
  },

  // ══ RAIN ══════════════════════════════════════════════════════════════════
  rain: {
    nav: (ctx, t) => {
      noise(ctx, t, 0.18, 0.14);          // rain burst
      osc(ctx, 1200, t + 0.05, 0.22, 0.08, 'sine', 400); // drop splash
      noise(ctx, t + 0.15, 0.14, 0.08);
      osc(ctx, 900,  t + 0.30, 0.20, 0.07, 'sine', 300);
    },
    tab: (ctx, t) => {
      noise(ctx, t, 0.06, 0.14);
      osc(ctx, 1400, t + 0.02, 0.10, 0.09, 'sine', 500);
    },
    filter: (ctx, t) => { noise(ctx, t, 0.03, 0.12); osc(ctx, 1800, t, 0.04, 0.07, 'sine', 2400); },
    click:  (ctx, t) => noise(ctx, t, 0.04, 0.15),
    message: (ctx, t) => {
      osc(ctx, 1600, t,        0.10, 0.10, 'sine', 600);
      noise(ctx, t + 0.05, 0.06, 0.08);
      osc(ctx, 1200, t + 0.14, 0.10, 0.08, 'sine', 500);
    },
    message_sent: (ctx, t) => { noise(ctx, t, 0.05, 0.10); osc(ctx, 800, t, 0.08, 0.08, 'sine', 400); },
    game_start: (ctx, t) => {
      noise(ctx, t, 0.20, 0.12);
      osc(ctx, 800, t + 0.05, 0.18, 0.10, 'sine', 1200);
      noise(ctx, t + 0.18, 0.15, 0.08);
      osc(ctx, 1200, t + 0.28, 0.18, 0.09, 'sine', 600);
    },
    game_win: (ctx, t) => {
      noise(ctx, t, 0.25, 0.10);
      seq(ctx, [800, 1000, 1200, 1400, 1600], t + 0.05, 0.10, 0.16, 0.09, 'sine');
      noise(ctx, t + 0.60, 0.20, 0.07);
    },
    exercise_start: (ctx, t) => {
      windNoise(ctx, t, 0.8, 0.06);
      noise(ctx, t + 0.15, 0.10, 0.10);
      osc(ctx, 600, t + 0.20, 0.30, 0.07, 'sine');
    },
    exercise_done: (ctx, t) => {
      noise(ctx, t, 0.15, 0.10);
      osc(ctx, 900, t + 0.08, 0.20, 0.09, 'sine', 300);
      noise(ctx, t + 0.25, 0.10, 0.06);
    },
    gift: (ctx, t) => {
      noise(ctx, t, 0.08, 0.12);
      osc(ctx, 1200, t + 0.04, 0.16, 0.10, 'sine', 500);
      noise(ctx, t + 0.16, 0.08, 0.08);
      osc(ctx, 1000, t + 0.22, 0.18, 0.09, 'sine', 400);
    },
    notif: (ctx, t) => { noise(ctx, t, 0.04, 0.10); osc(ctx, 1400, t + 0.02, 0.08, 0.09, 'sine', 700); },
  },

  // ══ MEDITATION ════════════════════════════════════════════════════════════
  meditation: {
    // Deep Tibetan bowl resonance — long sustain
    nav: (ctx, t) => {
      osc(ctx, 174, t, 1.20, 0.16, 'sine');           // F3 bowl
      osc(ctx, 261, t + 0.10, 1.00, 0.06, 'sine');    // C4 harmonic
      osc(ctx, 349, t + 0.25, 0.80, 0.04, 'sine');    // F4 overtone
      osc(ctx, 523, t + 0.45, 0.55, 0.03, 'sine');    // high shimmer
    },
    tab: (ctx, t) => {
      osc(ctx, 261, t, 0.40, 0.11, 'sine');            // C4 bowl tap
      osc(ctx, 392, t + 0.15, 0.30, 0.05, 'sine');
    },
    filter: (ctx, t) => osc(ctx, 523, t, 0.10, 0.06, 'sine'),
    click:  (ctx, t) => osc(ctx, 349, t, 0.08, 0.08, 'sine'),
    message: (ctx, t) => {
      osc(ctx, 329, t,        0.30, 0.09, 'sine');
      osc(ctx, 440, t + 0.20, 0.30, 0.06, 'sine');
    },
    message_sent: (ctx, t) => { osc(ctx, 392, t, 0.22, 0.08, 'sine'); osc(ctx, 523, t + 0.18, 0.20, 0.05, 'sine'); },
    game_start: (ctx, t) => {
      osc(ctx, 174, t,        1.00, 0.14, 'sine');     // deep bowl
      osc(ctx, 261, t + 0.30, 0.80, 0.07, 'sine');    // C4
      osc(ctx, 392, t + 0.55, 0.70, 0.05, 'sine');    // G4
      osc(ctx, 523, t + 0.75, 0.60, 0.04, 'sine');    // C5
    },
    game_win: (ctx, t) => {
      osc(ctx, 174, t, 1.20, 0.14, 'sine');
      seq(ctx, [261, 329, 392, 523, 659], t + 0.20, 0.15, 0.45, 0.06, 'sine');
      osc(ctx, 1047, t + 1.00, 0.60, 0.03, 'sine');
    },
    exercise_start: (ctx, t) => {
      osc(ctx, 110, t, 1.40, 0.15, 'sine');            // A2 — deep resonance
      osc(ctx, 220, t + 0.20, 1.00, 0.06, 'sine');
      osc(ctx, 440, t + 0.50, 0.70, 0.03, 'sine');
    },
    exercise_done: (ctx, t) => {
      seq(ctx, [523, 440, 349, 261], t, 0.20, 0.45, 0.08, 'sine');
      osc(ctx, 174, t + 0.90, 0.80, 0.12, 'sine');
    },
    gift: (ctx, t) => {
      chord(ctx, [174, 261, 349, 523], t, 1.00, 0.07, 'sine');
      osc(ctx, 784, t + 0.40, 0.55, 0.03, 'sine');
    },
    notif: (ctx, t) => osc(ctx, 261, t, 0.35, 0.10, 'sine'),
  },

  // ══ COSMOS ════════════════════════════════════════════════════════════════
  cosmos: {
    nav: (ctx, t) => {
      osc(ctx, 55,  t,        1.20, 0.10, 'sine');
      osc(ctx, 110, t + 0.10, 1.00, 0.07, 'sine', 165);
      osc(ctx, 660, t + 0.30, 0.80, 0.04, 'sine', 440);
      osc(ctx, 1320,t + 0.60, 0.50, 0.02, 'sine');
    },
    tab: (ctx, t) => {
      osc(ctx, 220, t,        0.40, 0.08, 'sine', 330);
      osc(ctx, 880, t + 0.15, 0.30, 0.03, 'sine');
    },
    filter: (ctx, t) => osc(ctx, 1760, t, 0.20, 0.04, 'sine', 880),
    click:  (ctx, t) => osc(ctx, 330, t, 0.18, 0.06, 'sine', 220),
    message: (ctx, t) => {
      osc(ctx, 330, t,        0.30, 0.08, 'sine', 495);
      osc(ctx, 660, t + 0.20, 0.30, 0.05, 'sine');
      osc(ctx, 990, t + 0.35, 0.25, 0.03, 'sine');
    },
    message_sent: (ctx, t) => {
      osc(ctx, 440, t,        0.15, 0.06, 'sine', 660);
      osc(ctx, 880, t + 0.12, 0.15, 0.03, 'sine');
    },
    game_start: (ctx, t) => {
      seq(ctx, [110, 165, 220, 330, 440], t, 0.14, 0.40, 0.07, 'sine');
      chord(ctx, [220, 330, 440, 660], t + 0.70, 0.90, 0.05);
      osc(ctx, 1320, t + 0.80, 0.60, 0.02, 'sine');
    },
    game_win: (ctx, t) => {
      seq(ctx, [220, 330, 440, 660, 880, 1320], t, 0.12, 0.50, 0.06, 'sine');
      chord(ctx, [440, 660, 880, 1320], t + 0.75, 1.00, 0.04);
    },
    exercise_start: (ctx, t) => {
      osc(ctx, 55,  t,        1.50, 0.12, 'sine');
      osc(ctx, 110, t + 0.30, 1.10, 0.06, 'sine', 165);
      osc(ctx, 440, t + 0.70, 0.70, 0.03, 'sine');
    },
    exercise_done: (ctx, t) => {
      seq(ctx, [880, 660, 440, 220, 110], t, 0.16, 0.50, 0.07, 'sine');
      osc(ctx, 55, t + 0.85, 0.80, 0.10, 'sine');
    },
    gift: (ctx, t) => {
      chord(ctx, [110, 220, 330, 440], t, 1.20, 0.06, 'sine');
      seq(ctx, [440, 660, 880, 1320], t + 0.30, 0.14, 0.40, 0.04, 'sine');
    },
    notif: (ctx, t) => {
      osc(ctx, 440, t,        0.20, 0.08, 'sine', 660);
      osc(ctx, 880, t + 0.15, 0.20, 0.04, 'sine');
    },
  },

  // ══ DOJO ══════════════════════════════════════════════════════════════════
  dojo: {
    nav: (ctx, t) => {
      noise(ctx, t, 0.04, 0.22);
      osc(ctx, 146, t + 0.02, 0.60, 0.18, 'sine');
      osc(ctx, 293, t + 0.10, 0.45, 0.10, 'sine');
      osc(ctx, 440, t + 0.22, 0.35, 0.06, 'sine');
    },
    tab: (ctx, t) => {
      noise(ctx, t, 0.025, 0.14);
      osc(ctx, 293, t + 0.01, 0.25, 0.12, 'sine');
    },
    filter: (ctx, t) => { noise(ctx, t, 0.018, 0.10); osc(ctx, 880, t, 0.10, 0.05, 'sine'); },
    click:  (ctx, t) => { noise(ctx, t, 0.020, 0.12); osc(ctx, 220, t, 0.12, 0.08, 'sine'); },
    message: (ctx, t) => {
      noise(ctx, t, 0.03, 0.15);
      osc(ctx, 440, t + 0.05, 0.35, 0.10, 'sine');
      osc(ctx, 587, t + 0.20, 0.25, 0.07, 'sine');
    },
    message_sent: (ctx, t) => {
      noise(ctx, t, 0.02, 0.10);
      osc(ctx, 587, t + 0.03, 0.20, 0.09, 'sine');
    },
    game_start: (ctx, t) => {
      noise(ctx, t,        0.05, 0.25);
      noise(ctx, t + 0.18, 0.05, 0.25);
      noise(ctx, t + 0.36, 0.05, 0.25);
      osc(ctx, 146, t + 0.40, 0.80, 0.18, 'sine');
      chord(ctx, [293, 440, 587], t + 0.50, 0.60, 0.08);
    },
    game_win: (ctx, t) => {
      seq(ctx, [293, 440, 587, 880], t, 0.10, 0.30, 0.14, 'sine');
      noise(ctx, t + 0.40, 0.06, 0.20);
      chord(ctx, [293, 440, 587, 880], t + 0.48, 0.70, 0.08);
    },
    exercise_start: (ctx, t) => {
      noise(ctx, t,        0.04, 0.20);
      noise(ctx, t + 0.25, 0.04, 0.20);
      osc(ctx, 146, t + 0.30, 1.00, 0.16, 'sine');
      osc(ctx, 293, t + 0.50, 0.70, 0.09, 'sine');
    },
    exercise_done: (ctx, t) => {
      noise(ctx, t, 0.04, 0.18);
      osc(ctx, 587, t + 0.05, 0.50, 0.14, 'sine');
      osc(ctx, 293, t + 0.30, 0.60, 0.10, 'sine');
      osc(ctx, 146, t + 0.55, 0.60, 0.14, 'sine');
    },
    gift: (ctx, t) => {
      noise(ctx, t, 0.035, 0.20);
      osc(ctx, 293, t + 0.04, 0.55, 0.14, 'sine');
      osc(ctx, 440, t + 0.20, 0.45, 0.10, 'sine');
      osc(ctx, 587, t + 0.35, 0.40, 0.08, 'sine');
    },
    notif: (ctx, t) => { noise(ctx, t, 0.025, 0.14); osc(ctx, 440, t + 0.02, 0.22, 0.10, 'sine'); },
  },
};

// ── Preview API (no license gate — let users hear before upgrading) ───────────

export function previewThemeSound(themeId, type = 'gift') {
  try {
    const fn = themes[themeId]?.[type];
    if (!fn) return;
    const ctx = getCtx();
    if (!ctx) return;
    const schedule = () => { fn(ctx, ctx.currentTime + 0.01); closeCtx(ctx); };
    if (ctx.state === 'suspended') {
      ctx.resume().then(schedule).catch(() => {});
    } else {
      schedule();
    }
  } catch {}
}

// ── Main play API ─────────────────────────────────────────────────────────────

export function playSound(type) {
  try {
    if (!isPaidUser()) return;
    const theme = localStorage.getItem('sl_sound_theme');
    if (!theme || theme === 'off') return;
    const fn = themes[theme]?.[type] ?? themes.tech?.[type];
    if (!fn) return;
    const ctx = getCtx();
    if (!ctx) return;
    const schedule = () => { fn(ctx, ctx.currentTime + 0.01); closeCtx(ctx); };
    if (ctx.state === 'suspended') {
      ctx.resume().then(schedule).catch(() => {});
    } else {
      schedule();
    }
  } catch {}
}

// ── Ambient music ─────────────────────────────────────────────────────────────
// Plays a looping background soundscape — designed for Lab & exercise sessions.
// Each theme has a 4-second pattern that reschedules itself continuously.

const AMBIENT_CYCLE = 4.0; // seconds per ambient cycle

const ambientPatterns = {
  // Pulsing low drone + high shimmer
  tech: (ctx, t) => {
    osc(ctx, 55, t, AMBIENT_CYCLE * 0.9, 0.03, 'sine');           // A1 drone
    osc(ctx, 110, t, AMBIENT_CYCLE * 0.9, 0.015, 'sine');          // A2 harmonic
    osc(ctx, 1320, t + 1.0, 0.35, 0.018, 'sine');                  // shimmer
    osc(ctx, 880,  t + 2.5, 0.25, 0.012, 'sine');                  // mid-pulse
    osc(ctx, 1320, t + 3.2, 0.25, 0.012, 'sine');
  },
  // Barely-there chord pad — meditative
  soft: (ctx, t) => {
    chord(ctx, [130, 196, 261, 330], t, AMBIENT_CYCLE * 0.95, 0.018, 'sine'); // C2+G2+C3+E3
    osc(ctx, 523, t + 1.5, 1.0, 0.010, 'sine');                   // quiet high C
  },
  // Wind breath + occasional nature tones
  nature: (ctx, t) => {
    windNoise(ctx, t, AMBIENT_CYCLE, 0.025);
    osc(ctx, 1047, t + 1.2, 0.35, 0.025, 'sine');                  // chime 1
    osc(ctx, 1319, t + 2.8, 0.30, 0.020, 'sine');                  // chime 2
  },
  // Quiet chiptune arpeggio
  arcade: (ctx, t) => {
    seq(ctx, [110, 165, 220, 165], t, 0.7, 0.3, 0.012, 'square');  // A2-E3-A3 loop
    osc(ctx, 330, t + 2.8, 0.20, 0.010, 'square');                  // accent
  },
  // Warm jazz chord pad
  jazz: (ctx, t) => {
    chord(ctx, [147, 196, 247, 294], t, AMBIENT_CYCLE * 0.9, 0.016, 'sine'); // Dm7 pad
    osc(ctx, 392, t + 1.8, 0.50, 0.008, 'sine');                              // inner melody note
    osc(ctx, 440, t + 3.2, 0.40, 0.008, 'sine');
  },
  // Low brass drone + shimmer
  epic: (ctx, t) => {
    osc(ctx, 65,  t, AMBIENT_CYCLE * 0.95, 0.022, 'sawtooth');       // deep pedal
    osc(ctx, 130, t, AMBIENT_CYCLE * 0.80, 0.008, 'sine');
    osc(ctx, 523, t + 2.0, 0.55, 0.012, 'sine');                      // high note swell
  },
  // Rainfall ambience
  rain: (ctx, t) => {
    windNoise(ctx, t, AMBIENT_CYCLE, 0.030);
    noise(ctx, t + 1.0, 0.06, 0.020);
    noise(ctx, t + 2.3, 0.05, 0.018);
    noise(ctx, t + 3.5, 0.06, 0.016);
  },
  // Tibetan bowl sustain
  meditation: (ctx, t) => {
    osc(ctx, 174, t, AMBIENT_CYCLE * 0.95, 0.020, 'sine');            // F3 bowl
    osc(ctx, 261, t + 1.0, 0.80, 0.008, 'sine');                      // harmonic bloom
    osc(ctx, 349, t + 2.8, 0.50, 0.005, 'sine');                      // upper overtone
  },
};

let _ambCtx    = null;
let _ambAlive  = false;

function _scheduleAmbient(ctx, themeId, nextT) {
  if (!_ambAlive || ctx !== _ambCtx) return;
  ambientPatterns[themeId]?.(ctx, nextT);
  const delayMs = Math.max(50, (nextT - ctx.currentTime + AMBIENT_CYCLE - 0.15) * 1000);
  setTimeout(() => _scheduleAmbient(ctx, themeId, nextT + AMBIENT_CYCLE), delayMs);
}

export function startAmbient(themeId) {
  if (!isPaidUser()) return;
  const theme = themeId || localStorage.getItem('sl_sound_theme');
  if (!theme || theme === 'off' || !ambientPatterns[theme]) return;
  stopAmbient();
  try {
    _ambCtx   = new (window.AudioContext || window.webkitAudioContext)();
    _ambAlive = true;
    _scheduleAmbient(_ambCtx, theme, _ambCtx.currentTime + 0.2);
  } catch {}
}

export function stopAmbient() {
  _ambAlive = false;
  if (_ambCtx) { try { _ambCtx.close(); } catch {} _ambCtx = null; }
}

// ── TTS exercise instructions ─────────────────────────────────────────────────
// Reads exercise instructions aloud using the Web Speech API.
// Available on Sensei+ (TTS is listed as a Sensei feature).

export function speakInstruction(text, lang = 'nl') {
  if (!isPaidUser()) return;
  const theme = localStorage.getItem('sl_sound_theme');
  if (!theme || theme === 'off') return;
  try {
    window.speechSynthesis?.cancel();
    const u       = new SpeechSynthesisUtterance(text);
    u.lang        = lang === 'nl' ? 'nl-NL' : 'en-US';
    u.rate        = 0.88;
    u.pitch       = 1;
    u.volume      = 0.90;
    window.speechSynthesis?.speak(u);
  } catch {}
}

export function stopSpeech() {
  try { window.speechSynthesis?.cancel(); } catch {}
}
